import React, { useState } from 'react';
import '../../App.css';
import { useAppSelector, useAppDispatch } from '../../app/hooks';

// images
import saveIcon from '../../icons/save.128.png';
import { Info, RefreshCw } from 'lucide-react';

// classes
import { ToolButtonType } from '../../tools/tool-button';
import ToolButton from '../../tools/tool-button';
import { HideDropdownMenu } from '../../utils/functions';
import SearchResultsTable from './search-results-table';
import SearchResultsFooter from '../../tools/search-results/search-results-footer';
import { useTranslation } from 'react-i18next';
import SaveQueryModal from './save-query-modal';
import Scrollbox from '../../tools/scrollbox';
import MiniButton from '../../tools/mini-button';

// types

// event-type imports.
import { Event, EventType, EventTarget, ExecuteADQLQuery } from '../../utils/events';

// types specifically for this component.
import { SelectedRowsType, LocationsType, QueryParameters } from './search-catalog-types';

//	--------------------------------------------------------------------------
//
//	R E D U X   S T A T E   M A N A G E M E N T
//
//	--------------------------------------------------------------------------

// import state definition and functions.
import { setDPTFilter, setPage, setPageSize } from './search-catalog-slice';
import { buildQueryDescription, getTapData } from './search-catalog-actions';
import { handleEvent } from '../../app-actions';

//	--------------------------------------------------------------------------
//
//	T Y P E S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	P R O P E R T I E S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	C L A S S   D E F I N I T I O N
//
//	--------------------------------------------------------------------------

export default function SearchCatalogResults(props: {
  key: string;
  query: QueryParameters;
  hidden: boolean;
}) {
  //
  //	--------------------------------------------------------------------------
  //
  //	R E D U X   S T A T E   H A N D L I N G
  //
  //	--------------------------------------------------------------------------

  const dispatch = useAppDispatch();

  // GLOBAL STATE VARIABLES:
  // ------------------------------

  const sG_Project: { id: number; name?: string } | undefined = useAppSelector(
    (state) => state.global.project,
  );
  const sG_DarkMode: boolean = useAppSelector((state) => state.global.darkMode);

  // LOCAL STATE VARIABLES:
  // ------------------------------

  const sDPTFilter: string = useAppSelector((state) => state.searchCatalog.dptFilter);
  const sDPTInResults: string[] = useAppSelector(
    (state) => state.searchCatalog.dptInResults,
  );
  const sDPTRetrieved: boolean = useAppSelector(
    (state) => state.searchCatalog.dptRetrieved,
  );
  const sTotalAllDPTs: { dpt: string; total: number }[] = useAppSelector(
    (state) => state.searchCatalog.total,
  );
  const sTotal: number =
    sTotalAllDPTs.find((item) => item.dpt === sDPTFilter)?.total ?? 0;
  const sPages: number = useAppSelector((state) => state.searchCatalog.pages);
  const sPage: number = useAppSelector((state) => state.searchCatalog.page);
  const sPageSize: number = useAppSelector((state) => state.searchCatalog.pageSize);
  const sBuiltQuery: string | undefined = useAppSelector(
    (state) => state.searchCatalog.builtQuery,
  );

  //	-------------------------------------------------
  //
  //	state variables
  //
  //	-------------------------------------------------

  const { t } = useTranslation();

  const [sShowModal, setShowModal] = useState(false);

  //	-------------------------------------------------
  //
  //	handler for button onClick events.
  //
  //	-------------------------------------------------

  const onClickBtnHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    //
    event.preventDefault();

    const button: HTMLButtonElement = event.currentTarget;

    console.log('button.name:');
    console.log(button.name);

    if (button.id === 'save-query-button') {
      setShowModal(true);
    }
    if (button.id === 'refresh-query-button') {
      dispatch(getTapData({ query: props.query }));
    }

    // open the current query in the ADQL editor.
    if (button.name === 'adql-editor') {
      //
      event.stopPropagation();

      if (sBuiltQuery !== undefined) {
        //
        const params: ExecuteADQLQuery = {
          adqlQuery: sBuiltQuery,
          displayOnly: true,
        };

        const newEvent: Event = {
          eventTarget: EventTarget.SEARCH_CATALOG_FORM,
          eventType: EventType.EXECUTE_ADQL_QUERY,
          parameters: params,
        };

        dispatch(
          handleEvent({
            event: newEvent,
          }),
        );
        //
      }
      //
    }
    //
  }; // onClickBtnHandler

  //	-------------------------------------------------
  //
  //	handler for div onClick events.
  //
  //	-------------------------------------------------

  const onClickDivHandler = (event: React.MouseEvent<HTMLDivElement>) => {
    //
    const div: HTMLDivElement = event.currentTarget;

    // if the user selects an item on the short description, then stop the propagation so that the panel doesn't expand.
    if (div.id === 'short-query-description' || div.id === 'long-query-description') {
      const selection: Selection | null = window.getSelection();
      if (selection?.toString() ?? '' !== '') {
        event.stopPropagation();
      }
    }
    //
  }; // onClickDivHandler

  //	-------------------------------------------------
  //
  //	function that handles a change in the desired page size
  //
  //	-------------------------------------------------

  const pageSizeOnChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
    //
    var newPageSize: number = sPageSize ?? 10;
    if (Number(event.target.value)) {
      newPageSize = parseInt(event.target.value);
    }

    // update state with the selected value.
    dispatch(setPageSize(newPageSize));
    //
  }; // pageSizeOnChangeHandler

  //	------------------------------------------------------------
  //
  //	Handles an update page event from the results footer.
  //
  //	------------------------------------------------------------

  const updatePage = function (args: { page: number }) {
    //
    dispatch(setPage(args.page));
    //
  }; // updatePage

  //	--------------------------------------------------------------------------
  //
  //	H O O K S
  //
  //	--------------------------------------------------------------------------

  //	--------------------------------------------------------------------------
  //
  //	C O M P O N E N T   C O D E
  //
  //	--------------------------------------------------------------------------

  return (
    <div className="search-results-tab" data-hidden={props.hidden === true ? 'T' : 'F'}>
      <div style={{ flex: '0 0 2px' }} />

      {/*
       //  query summary (single line, no expand/collapse per MAN-747)
       */}
      <div
        className="text-colour search-results-query-summary"
        style={{
          flex: '0 0 auto',
          margin: '10px 10px 0px 10px',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
        }}
      >
        <div style={{ flex: '0 0 5px' }} />
        <Info width={24} height={24} strokeWidth={1.5} aria-hidden="true" />
        <div style={{ flex: '0 0 20px' }} />
        <div
          id="short-query-description"
          style={{
            flex: '1 1',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            minHeight: '28px',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
          }}
        >
          {buildQueryDescription({
            query: props.query,
            builtQuery: sBuiltQuery,
          })}
        </div>
        <div style={{ flex: '0 0 5px' }} />
      </div>
      <div style={{ flex: '0 0 8px' }} />

      {/*
       //  expanded query panel hidden per MAN-747 (ADQL editor, full ADQL text)
       */}
      <div style={{ display: 'none' }}>
        {/* the column containing filter boxes, etc. */}
        <Scrollbox direction="vertical" width="100%" flex="1 1" flexDirection="column">
          <div style={{ flex: '0 0 5px' }} />
          <div
            style={{ flex: '1 1', display: 'flex', flexDirection: 'row', width: '100%' }}
          >
            <div style={{ flex: '0 0 5px' }} />
            <Info width={24} height={24} strokeWidth={1.5} aria-hidden="true" />
            <div style={{ flex: '0 0 20px' }} />
            <div
              id="long-query-description"
              style={{
                flex: '1 1',
                display: 'flex',
                flexDirection: 'column',
                marginTop: '2px',
                minWidth: '1px',
              }}
              onClick={onClickDivHandler}
            >
              <div
                style={{
                  flex: '0 0 auto',
                  display:
                    props.query.adqlQuery === undefined || props.query.adqlQuery === ''
                      ? 'flex'
                      : 'none',
                  flexDirection: 'row',
                  alignItems: 'center',
                  width: '100%',
                  padding: '0px 0px 10px 0px',
                }}
              >
                {buildQueryDescription({
                  query: props.query,
                  builtQuery: sBuiltQuery,
                  longFormat: true,
                })}
              </div>
              <div style={{ flex: '0 0 auto', width: '100%' }}>ADQL query:</div>
              <div style={{ flex: '0 0 5px' }} />
              <div
                style={{
                  flex: '0 0 auto',
                  border: sG_DarkMode ? '1pt solid #555' : '1pt solid #999',
                  borderRadius: '6px',
                  width: '100%',
                  padding: '4px 4px 4px 4px',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                }}
              >
                {sBuiltQuery !== undefined ? sBuiltQuery : 'running.....'}
              </div>
              <div style={{ flex: '1 1' }}></div>
            </div>
            <div style={{ flex: '0 0 5px' }} />
          </div>
        </Scrollbox>
        <div style={{ flex: '0 0 5px' }} />
        <div
          style={{
            flex: '0 0 40px',
            display: 'flex',
            flexDirection: 'row',
            width: '100%',
            padding: '0px 5px 0px 5px',
          }}
        >
          {/* Save query hidden per MAN-747 */}
          <div style={{ display: 'none' }}>
            <ToolButton
              key={t('Save query')}
              name="save-query-button"
              onClick={onClickBtnHandler}
              text={t('Save query')}
              type={ToolButtonType.SECONDARY}
            />
            <div style={{ flex: '0 0 5px' }} />
          </div>
          <ToolButton
            key={t('ADQL editor')}
            name="adql-editor"
            onClick={onClickBtnHandler}
            text={t('ADQL editor')}
            type={ToolButtonType.SECONDARY}
          />
        </div>
        <div style={{ flex: '0 0 5px' }} />
        <div
          style={{
            flex: '0 0 16px',
            display: 'flex',
            flexDirection: 'row',
            position: 'absolute',
            bottom: '-8px',
            width: '100%',
          }}
        >
          <div style={{ flex: '1 1' }} />
          <div
            style={{
              border: '1pt solid #555',
              flex: '0 0 20px',
              height: '15px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '5pt',
              backgroundColor: '#040034',
              color: 'white',
              fontWeight: '800',
            }}
          >
            ⋀
          </div>
          <div style={{ flex: '1 1' }} />
        </div>
      </div>

      {/* Data product type filter dropdown */}
      <div
        className="search-results-filter-bar"
        style={{
          padding: '10px',
        }}
      >
        <div
          className="fr br-d-b sc-results-filter-row"
          style={{
            paddingBottom: '15px',
          }}
        >
          {/* Save query hidden per MAN-747 */}
          <div style={{ display: 'none' }}>
            <MiniButton
              id="save-query-button"
              data-selected="F"
              onClick={onClickBtnHandler}
            >
              <img
                src={saveIcon}
                role="decoration"
                alt=""
                width="28"
                height="28"
                title={t('Save query to project')}
              />
            </MiniButton>
            <div style={{ flex: '0 0 5px' }} />
          </div>
          {/* Re-run query hidden per MAN-747 */}
          <div className="sc-results-filter-type">
            <MiniButton
              id="refresh-query-button"
              data-selected="F"
              onClick={onClickBtnHandler}
              className="text-colour"
              data-mini-button="T"
              style={{
                marginRight: '15px',
              }}
            >
              <RefreshCw width={30} height={30} strokeWidth={1.5} />
            </MiniButton>
            <label id="search-catalog-filter-by-type-label" className="text-colour">
              {t('Filter results by type:')}
            </label>
            <select
              aria-labelledby="search-catalog-filter-by-type-label"
              value={sDPTFilter ?? '<all>'}
              onChange={(e) => dispatch(setDPTFilter(e.target.value))}
              className="listbox sc-dpt-select"
              disabled={sDPTInResults.length === 1}
              data-disabled={sDPTInResults.length === 1 ? 'T' : 'F'}
            >
              {sDPTInResults.map((dpt, index) => (
                <option key={index} value={dpt}>
                  {(dpt === '<all>' ? 'All' : dpt !== '' ? dpt : '<none>') +
                    ' [' +
                    (
                      sTotalAllDPTs.find((item) => item.dpt === dpt)?.total ?? 0
                    ).toString() +
                    ' result(s)]'}
                </option>
              ))}
            </select>
          </div>
          <div className="sc-results-filter-pagination">
            <label id="search-catalog-results-per-page-label" className="text-colour">
              {t('Results per page:')}
            </label>
            <select
              area-labelledby="search-catalog-results-per-page-label"
              name="searchResultsPerPage"
              onChange={pageSizeOnChangeHandler}
              value={sPageSize}
              className="listbox sc-results-per-page-select"
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
            </select>
          </div>
        </div>
      </div>

      {/* the results component contains results for each data product type. all but one of these result sets will
       // be hidden */}
      <div className="search-results-table-holder-visible">
        <SearchResultsTable
          key={'dpt_' + sDPTFilter + '_' + sPage.toString() + '_' + sPageSize.toString()}
        />
      </div>

      {/* the search-results footer contains the navigation buttons for moving between results pages */}
      <SearchResultsFooter
        key={
          sPages.toString() +
          '_' +
          sTotal.toString() +
          '_' +
          sPage.toString() +
          '_' +
          sPageSize.toString()
        }
        page={sPage}
        pageSize={sPageSize}
        pages={sPages}
        rows={sTotal}
        updatePage={updatePage}
        visible={true}
      />

      <div style={{ display: 'none' }}>
        <SaveQueryModal
          show={sShowModal}
          handleClose={() => setShowModal(false)}
          positionSearch={
            props.query.ra !== undefined &&
            props.query.dec !== undefined &&
            props.query.fov !== undefined
          }
          query={props.query}
          project={sG_Project}
        />
      </div>
    </div>
  );
} // SearchCatalogResults

//	--------------------------------------------------------------------------
//
//	S U B   C O M P O N E N T S
//
//	--------------------------------------------------------------------------
